'use client'

import { TCardFacet, TFacet } from '@/types/collection'
import { usePathname } from 'next/navigation'
import { useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { getPreservedParamsForCollection } from '../constants'
import { useCollectionFilterStoreV2 } from '../store/collection-filter-store-v2'
import { urlBuilder } from '../utils/url-builder.service'

// Helper function để build URL using UrlBuilderService
const buildUrl = (
  originalSlug: string,
  filters: Record<string, string | string[] | number | undefined>,
  concatValue: string | null,
  pathname: string
) => {
  const preservedParams = getPreservedParamsForCollection(originalSlug)
  return urlBuilder.buildCollectionUrl({
    originalSlug,
    filters,
    concatValue,
    currentPathname: pathname,
    preserveParams: preservedParams.length > 0 ? preservedParams : undefined,
  })
}

export const useUrlSync = (facets?: TFacet[], cardFacets?: TCardFacet[]) => {
  const pathname = usePathname()
  const { filters, originalSlug, concatValue } = useCollectionFilterStoreV2(
    useShallow((state) => ({
      filters: state.filters,
      originalSlug: state.originalSlug,
      concatValue: state.concatValue,
    }))
  )

  // Validate URL vs store state (không update URL nữa vì đã update trong store)
  useEffect(() => {
    if (!originalSlug) return

    // Build expected URL từ store state sử dụng helper function
    const expectedUrl = buildUrl(originalSlug, filters, concatValue, pathname)

    // Validate URL consistency (silent check)
    if (expectedUrl !== pathname) {
      // URL inconsistency detected but not logged in production
    }
  }, [filters, originalSlug, pathname, concatValue, facets, cardFacets])

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      // Parse URL và update store với logic mới
      const url = urlBuilder.getCurrentUrl()

      if (originalSlug) {
        // Use parser with facets data for proper mapping
        const { filters: urlFilters, concatValue: parsedConcatValue } =
          urlBuilder.parseUrl(url, originalSlug, facets, cardFacets)

        // Update store with parsed filters and concat value atomically
        const { setFilters, setConcatValue } =
          useCollectionFilterStoreV2.getState()

        // Update both filters and concatValue together to avoid race conditions
        setFilters(urlFilters)
        setConcatValue(parsedConcatValue)
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [originalSlug, facets, cardFacets])
}
